/**
 * Next-period estimate and confidence.
 * See docs/12-derivation-and-prediction.md Sections 2-4.
 *
 * Two things in here are regulatory boundaries, not preferences:
 *
 *  1. Fewer than two completed cycles produces NO prediction (FR-404). A
 *     confident-looking wrong estimate on day one is worse than an honest
 *     blank.
 *  2. Nothing in this module or its callers may frame output as contraception,
 *     safe days, or a diagnosis (FR-406, L-08). Crossing that line makes Thana
 *     a regulated medical device in both the EU and the US.
 */
import { addDays } from './date'
import { validCycles } from './cycles'
import { mad, median } from './math'
import type { Cycle, DerivedCycles, ISODate } from './types'

/** Confirmed in Q11: long enough to be stable, short enough to track real change. */
export const PREDICTION_N = 6

export type Confidence = 'none' | 'low' | 'medium' | 'high'

export type Prediction =
  | { kind: 'none'; reason: 'insufficient-history' | 'suspended' }
  | {
      kind: 'estimate'
      predictedStart: ISODate
      /** How many completed, valid cycles the estimate rests on. */
      basedOn: number
      /** Median cycle length, whole days. */
      typicalLength: number
      /** Median absolute deviation, whole days. */
      variation: number
      confidence: Exclude<Confidence, 'none'>
      /** Estimated fertile window — present only at medium or high confidence. */
      fertileWindow?: { ovulation: ISODate; from: ISODate; to: ISODate }
    }

/**
 * Confidence bands. Evaluated highest-first; the first match wins.
 * Deterministic and published, so the UI can explain the reason rather than
 * showing a bare label or a percentage the model cannot justify.
 */
export function confidenceOf(cycles: readonly Cycle[]): Confidence {
  const n = cycles.length
  if (n < 2) return 'none'
  const variation = mad(cycles.map((c) => c.length))
  if (n >= 4 && variation <= 2) return 'high'
  if (n >= 3 && variation <= 4) return 'medium'
  return 'low'
}

export interface PredictionOptions {
  /** FR-412: pregnancy, post-partum, menopause, continuous contraception, or preference. */
  suspended?: boolean
}

export function predictNextPeriod(
  derived: DerivedCycles,
  options: PredictionOptions = {},
): Prediction {
  if (options.suspended) return { kind: 'none', reason: 'suspended' }

  const usable = validCycles(derived.cycles).slice(-PREDICTION_N)
  const lastPeriod = derived.periods[derived.periods.length - 1]

  if (usable.length < 2 || lastPeriod === undefined) {
    return { kind: 'none', reason: 'insufficient-history' }
  }

  const lengths = usable.map((c) => c.length)
  const typicalLength = median(lengths)
  const predictedStart = addDays(lastPeriod.start, typicalLength)
  const confidence = confidenceOf(usable) as Exclude<Confidence, 'none'>

  const prediction: Prediction = {
    kind: 'estimate',
    predictedStart,
    basedOn: usable.length,
    typicalLength,
    variation: mad(lengths),
    confidence,
  }

  // FR-405: shown only at medium or high confidence. A fertile-window estimate
  // built on an unstable cycle length is close to noise, and presenting noise
  // here has consequences.
  if (confidence === 'medium' || confidence === 'high') {
    // The 14-day luteal phase is a population convention, not a measurement of
    // this person. The UI must say so.
    const ovulation = addDays(predictedStart, -14)
    prediction.fertileWindow = {
      ovulation,
      from: addDays(ovulation, -5),
      to: addDays(ovulation, 1),
    }
  }

  return prediction
}
